import React, { useCallback, useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import KaAlert from '../Common/KaAlert';
import KaModal from '../Common/KaModal';
import SpinnerButton from '../Common/SpinnerButton';
import { CenteredSpinner } from '../Common/CenteredSpinner';
import PaginatedTable from '../Common/Pagination/PaginatedTable';
import useDisplayMessage from '../Common/useDisplayMessage';
import useInterval from '../Common/useInterval';
import FilterInput from '../FilterInput';
import useGetFieldAlyticsActiveOrders, { FieldAlyticsActiveOrder } from '../../requests/fieldalytics/useGetFieldAlyticsActiveOrders';
import useGetFieldAlyticsCompletedOrders, { FieldAlyticsCompletedOrder } from '../../requests/fieldalytics/useGetFieldAlyticsCompletedOrders';
import useGetFieldAlyticsErroredOrders, { FieldAlyticsErroredOrder } from '../../requests/fieldalytics/useGetFieldAlyticsErroredOrders';
import useMarkFieldAlyticsExported from '../../requests/fieldalytics/useMarkFieldAlyticsExported';
import { useUserOrKaEmployee } from '../UserAccess/useUserOrKaEmployee';
import { LoginType } from '../UserAccess/UserContext';
import { humanReadableSort } from '../../Utilities';

enum Tab { Active, Completed, Error }

const PAGE_SIZE = 25;

const sortByNumber = <T extends { number: string }>(orders: T[]) => humanReadableSort(orders, 'number');

const sortOrders = <T extends { number: string; error?: string }>(orders: T[]) =>
  sortByNumber(orders.filter(o => o.error)).concat(sortByNumber(orders.filter(o => !o.error)));

const paginate = <T,>(orders: T[], page: number) => orders.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

interface MarkExportedModalProps {
  number: string;
  show: boolean;
  onClose: () => void;
  onMarkedExported: () => void;
}

const MarkExportedModal = ({ number, show, onClose, onMarkedExported }: MarkExportedModalProps) => {
  const [notes, setNotes] = useState('');
  const displayMessage = useDisplayMessage();
  const markExported = useMarkFieldAlyticsExported({
    onSuccess: onMarkedExported,
    onError: (e) => displayMessage.fail(e.message),
  });

  return (
    <KaModal
      title="Mark Order As Exported"
      show={show}
      onHide={onClose}
      body={
        <>
          <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
          <p>Marking an order as exported will prevent the FieldAlytics integration from attempting to send totals back to FieldAlytics. It will also remove the order from the queue of unexported orders in Constellation. This action cannot be undone.</p>
          <p>You may provide notes about why the order was marked as exported for future reference.</p>
          <label className="form-label mt-2" htmlFor="txtExportedNotes">Notes:</label>
          <textarea id="txtExportedNotes" value={notes} rows={4} onChange={ev => setNotes(ev.target.value)} className="form-control" />
        </>
      }
      footer={
        <SpinnerButton className="btn-ghost-danger" spinning={markExported.isLoading} onClick={() => markExported.request({ number, notes })}>
          Mark As Exported
        </SpinnerButton>
      }
    />
  );
};

interface OrderTabProps {
  orderNumber: string;
  refresh: boolean;
  refreshCallback: () => void;
  displayMessageFail: (message: string) => void;
}

const ActiveOrders = ({ orderNumber, refresh, refreshCallback, displayMessageFail }: OrderTabProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const getActiveOrders = useGetFieldAlyticsActiveOrders(orderNumber, {
    onSuccess: (orders: FieldAlyticsActiveOrder[]) => setTotalPages(Math.max(1, Math.ceil(orders.length / PAGE_SIZE))),
    onError: (e) => displayMessageFail(e.message),
  });

  useEffect(() => setCurrentPage(0), [orderNumber]);

  const refreshActiveOrders = getActiveOrders.query;

  useEffect(() => {
    if (refresh) {
      refreshActiveOrders();
      refreshCallback();
    }
  }, [refresh, refreshCallback, refreshActiveOrders]);

  useInterval(() => getActiveOrders.query(), 90000, false);

  const orders = getActiveOrders.data ?? [];

  return (
    <div className="tab-pane active show">
      {getActiveOrders.isLoading ? <CenteredSpinner /> : (
        <PaginatedTable totalPages={totalPages} currentPage={currentPage} pageSize={PAGE_SIZE} onPageChange={setCurrentPage}>
          <table className="table table-hover table-bordered">
            <thead>
              <tr>
                <th className="w-25">Number</th>
                <th className="w-75">Status</th>
              </tr>
            </thead>
            <tbody>
              {paginate(sortOrders(orders), currentPage).map(ao => (
                <tr key={ao.number}>
                  <td>{ao.number}</td>
                  <td>
                    {ao.error
                      ? <span className="text-danger">{ao.error}</span>
                      : <span className="text-success">Imported Successfully</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </PaginatedTable>
      )}
    </div>
  );
};

const ErrorOrders = ({ orderNumber, refresh, refreshCallback, displayMessageFail }: OrderTabProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const getErroredOrders = useGetFieldAlyticsErroredOrders(orderNumber, {
    onSuccess: (orders: FieldAlyticsErroredOrder[]) => setTotalPages(Math.max(1, Math.ceil(orders.length / PAGE_SIZE))),
    onError: (e) => displayMessageFail(e.message),
  });

  useEffect(() => setCurrentPage(0), [orderNumber]);

  const refreshErroredOrders = getErroredOrders.query;

  useEffect(() => {
    if (refresh) {
      refreshErroredOrders();
      refreshCallback();
    }
  }, [refresh, refreshCallback, refreshErroredOrders]);

  useInterval(() => getErroredOrders.query(), 90000, false);

  const orders = getErroredOrders.data ?? [];

  return (
    <div className="tab-pane active show">
      {getErroredOrders.isLoading ? <CenteredSpinner /> : (
        <PaginatedTable totalPages={totalPages} currentPage={currentPage} pageSize={PAGE_SIZE} onPageChange={setCurrentPage}>
          <table className="table table-hover table-bordered">
            <thead>
              <tr>
                <th className="w-25">Number</th>
                <th className="w-75">Reason</th>
              </tr>
            </thead>
            <tbody>
              {paginate(sortByNumber(orders), currentPage).map(eo => (
                <tr key={eo.number}>
                  <td>{eo.number}</td>
                  <td className="text-danger">{eo.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </PaginatedTable>
      )}
    </div>
  );
};

interface CompletedOrdersTabProps extends OrderTabProps {
  markOrderExported?: (number: string) => void;
}

const CompletedOrders = ({ orderNumber, refresh, refreshCallback, markOrderExported, displayMessageFail }: CompletedOrdersTabProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const getCompletedOrders = useGetFieldAlyticsCompletedOrders(orderNumber, {
    onSuccess: (orders: FieldAlyticsCompletedOrder[]) => setTotalPages(Math.max(1, Math.ceil(orders.length / PAGE_SIZE))),
    onError: (e) => displayMessageFail(e.message),
  });

  useEffect(() => setCurrentPage(0), [orderNumber]);

  const refreshCompletedOrders = getCompletedOrders.query;

  useEffect(() => {
    if (refresh) {
      refreshCompletedOrders();
      refreshCallback();
    }
  }, [refresh, refreshCallback, refreshCompletedOrders]);

  useInterval(() => getCompletedOrders.query(), 90000, false);

  const orders = getCompletedOrders.data ?? [];

  return (
    <div className="tab-pane active show">
      {getCompletedOrders.isLoading ? <CenteredSpinner /> : (
        <PaginatedTable totalPages={totalPages} currentPage={currentPage} pageSize={PAGE_SIZE} onPageChange={setCurrentPage}>
          <table className="table table-hover table-bordered">
            <thead>
              <tr>
                <th className="w-25">Number</th>
                <th className="w-75">Export Status</th>
              </tr>
            </thead>
            <tbody>
              {paginate(sortOrders(orders), currentPage).map(co => (
                <tr key={co.number}>
                  <td>{co.number}</td>
                  <td>
                    {co.error
                      ? (
                        <span className="text-danger">
                          {co.error}
                          {markOrderExported &&
                            <SpinnerButton className="btn-ghost-danger btn-sm ms-2" spinning={false} onClick={() => markOrderExported(co.number)}>
                              Mark Exported
                            </SpinnerButton>}
                        </span>
                      )
                      : (
                        <span className="text-success">
                          Exported successfully at: {co.exportedAt ? new Date(co.exportedAt).toLocaleString() : 'No export date available'}
                          {co.exportMessage && <p className="mt-5 mb-0">{co.exportMessage}</p>}
                        </span>
                      )
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </PaginatedTable>
      )}
    </div>
  );
};

export const FieldAlyticsOrderPage = () => {
  const { userType } = useUserOrKaEmployee();
  const canEdit = userType === LoginType.User;
  const displayMessage = useDisplayMessage();
  const [tab, setTab] = useState<Tab>(Tab.Active);
  const [numberSearch, setNumberSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [refreshQuery, setRefreshQuery] = useState(false);
  const [orderToMarkExported, setOrderToMarkExported] = useState<string | null>(null);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(numberSearch), 600);
    return () => clearTimeout(handler);
  }, [numberSearch]);

  const markQueryRefreshed = useCallback(() => setRefreshQuery(false), []);

  const orderMarkedExported = () => {
    displayMessage.success(`Order ${orderToMarkExported} marked exported`);
    setOrderToMarkExported(null);
    setRefreshQuery(true);
  };

  return (
    <Container>
      {canEdit &&
        <MarkExportedModal
          show={!!orderToMarkExported}
          number={orderToMarkExported ?? ''}
          onClose={() => setOrderToMarkExported(null)}
          onMarkedExported={orderMarkedExported}
        />}
      <h2 className="ka-blue">FieldAlytics - Orders</h2>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <div className="row gx-2 gx-md-3 mb-3">
        <div className="col-md-4 mb-2 mb-md-0">
          <FilterInput placeholder="Filter orders by number" value={numberSearch} onFilterTextChange={setNumberSearch} />
        </div>
      </div>
      <div className="text-center position-relative">
        <button
          className="btn btn-primary btn-icon rounded-pill position-absolute top-0 start-0 my-1"
          aria-label="refresh"
          onClick={() => setRefreshQuery(true)}>
          <i className="bi bi-arrow-clockwise fs-5" />
        </button>
        <div className="row d-flex justify-content-center mb-1">
          <ul className="nav col-8 nav-tabs">
            <li className="nav-item col-4">
              <a className={`nav-link ${tab === Tab.Active ? 'active' : ''}`} href="#" onClick={e => { e.preventDefault(); setTab(Tab.Active); }}>Active Orders</a>
            </li>
            <li className="nav-item col-4">
              <a className={`nav-link ${tab === Tab.Completed ? 'active' : ''}`} href="#" onClick={e => { e.preventDefault(); setTab(Tab.Completed); }}>Completed Orders</a>
            </li>
            <li className="nav-item col-4">
              <a className={`nav-link ${tab === Tab.Error ? 'active' : ''}`} href="#" onClick={e => { e.preventDefault(); setTab(Tab.Error); }}>Errors</a>
            </li>
          </ul>
        </div>
      </div>
      <div className="tab-content active show">
        {tab === Tab.Active && <ActiveOrders orderNumber={debouncedSearch} refresh={refreshQuery} refreshCallback={markQueryRefreshed} displayMessageFail={displayMessage.fail} />}
        {tab === Tab.Completed && <CompletedOrders orderNumber={debouncedSearch} refresh={refreshQuery} refreshCallback={markQueryRefreshed} markOrderExported={canEdit ? setOrderToMarkExported : undefined} displayMessageFail={displayMessage.fail} />}
        {tab === Tab.Error && <ErrorOrders orderNumber={debouncedSearch} refresh={refreshQuery} refreshCallback={markQueryRefreshed} displayMessageFail={displayMessage.fail} />}
      </div>
    </Container>
  );
};
